import * as Core from "@blaze-cardano/core";
import type {
  IDepositHistoryTx,
  TDepositHistoryStatus,
} from "@/context/GummiwormContext/hooks/useDepositTxs";

/**
 * Pure deposit lifecycle predicates, shared by the reconciler in
 * GummiwormProvider and the DepositOverlay timeline UI.
 *
 * Terminology: an L2 block is "hard confirmed" once the head peers have
 * multisigned it. Two sources can tell us:
 *
 * 1. The unified API's hydrozoa proxy: GET /head/requests/{id} (hydrozoa
 *    data, proxied by api/src/hydrozoa.rs — the frontend never talks to
 *    hydrozoa directly) reports status.type === "HARD_CONFIRMED" (see
 *    polledStatusUpdate below). This is the authoritative source in the
 *    current any-remote deployment.
 * 2. The websocket ledger view (deposit.refundTx, set on Event::BlockConfirmed).
 *    NOTE: the hydrozoa↔ledger wire contract currently omits
 *    ProxyBlockConfirmation (types/src/types/coordination/command.rs), so this
 *    path never fires today — it is kept for when the contract gains it.
 *
 * A refund effect can be polled before the deposit's block is hard confirmed,
 * so the L1 deposit tx must never be submitted on the strength of a polled
 * refund alone.
 */

/** Statuses in which no further L1 submission work should happen. */
const SUBMISSION_TERMINAL_STATUSES: TDepositHistoryStatus[] = [
  "settling",
  "on-chain",
  "absorbed",
  "failed",
  "rejected",
  "refunded",
  "refunding",
];

export type TStageState = "done" | "active" | "waiting";

export interface IDepositStageStates {
  approved: TStageState;
  refund: TStageState;
  submitted: TStageState;
  available: TStageState;
}

type TStageInput = Pick<
  IDepositHistoryTx,
  "status" | "hardConfirmed" | "refundCbor" | "refundTxHash"
>;

/** The deposit's L2 block has been hard confirmed (multisigned by the head peers). */
export function isHardConfirmed(
  tx: Pick<IDepositHistoryTx, "status" | "hardConfirmed">,
): boolean {
  // "confirmed" is only ever set from the websocket BlockConfirmed path, so
  // it doubles as a hard-confirmation signal for entries persisted before
  // the explicit flag existed. "absorbed" implies confirmation by protocol.
  return (
    tx.hardConfirmed === true ||
    tx.status === "confirmed" ||
    tx.status === "absorbed"
  );
}

/** We hold the multisigned refund tx (cbor for download, hash for display). */
export function hasProvisionedRefund(
  tx: Pick<IDepositHistoryTx, "refundCbor" | "refundTxHash">,
): boolean {
  return !!tx.refundCbor && !!tx.refundTxHash;
}

/**
 * Fallback signature prompt. Deposits sign at click time (the signed tx is
 * held locally; the refund keys off the tx body hash, which witnesses do
 * not change, and nothing reaches L1 until the refund is in hand). This
 * predicate only fires for entries persisted by older builds that deferred
 * the signature until the refund arrived.
 */
export function shouldRequestSignature(tx: IDepositHistoryTx): boolean {
  return (
    !tx.cbor &&
    !!tx.unsignedCbor &&
    !!tx.refundCbor &&
    !SUBMISSION_TERMINAL_STATUSES.includes(tx.status)
  );
}

/**
 * Should the reconciler submit the signed deposit tx to L1 now?
 * Requires BOTH hard confirmation of the deposit's L2 block AND the refund
 * tx in hand, in addition to a collected signature.
 */
export function shouldSubmitDeposit(tx: IDepositHistoryTx): boolean {
  return (
    !!tx.cbor &&
    !!tx.refundCbor &&
    isHardConfirmed(tx) &&
    !tx.hash &&
    !SUBMISSION_TERMINAL_STATUSES.includes(tx.status)
  );
}

/** The parts of a GET /head/requests/{id} response this reads. */
export interface IPolledRequest {
  status?: { type?: string };
  absorptionDecisionStatus?: { decision?: string; blockNumber?: number };
  failureReason?: string | null;
}

/** Statuses past which a polled decision must not rewrite the entry. */
const DECISION_TERMINAL_STATUSES: TDepositHistoryStatus[] = [
  "absorbed",
  "rejected",
  "refunding",
  "refunded",
  "failed",
];

/**
 * Turn a polled GET /head/requests/{id} response into a deposit update.
 *
 * This reads the absorption decision as well as hard confirmation, and that
 * matters: absorption previously arrived ONLY over the websocket, and deposit
 * entries live in localStorage. A single missed push therefore stranded a
 * deposit on "Waiting for Settlement" permanently -- reloading restored the
 * stuck entry rather than reconciling it, and `needsRequestPolling` had already
 * given up. That happened on 2026-08-25, while the API was cycling and the
 * notifier was fanning out nothing during a views rebuild.
 *
 * An earlier note here said reading absorption in both places would
 * "double-source" it. The websocket is a fast path, not a reliable one, and
 * these promotions are idempotent -- so the poll costs nothing and removes the
 * single point of failure. The websocket still wins the race in the normal
 * case; the poll only catches what it dropped.
 *
 * Only an explicit HARD_CONFIRMED counts for confirmation — soft confirmation
 * is not enough to release the L1 submission. Returns null when there is
 * nothing to apply.
 */
export function polledStatusUpdate(
  tx: Pick<IDepositHistoryTx, "status" | "hash">,
  polled: IPolledRequest | null | undefined,
): Partial<IDepositHistoryTx> | null {
  if (!polled) return null;

  // A decision is the end of the story, so it outranks the confirmation
  // signal below -- but never rewrites an entry that already reached one.
  const decision = polled.absorptionDecisionStatus?.decision;
  if (!DECISION_TERMINAL_STATUSES.includes(tx.status)) {
    if (decision === "ABSORBED") {
      return { hardConfirmed: true, status: "absorbed" };
    }
    if (decision === "REJECTED") {
      // Hash-aware, exactly as the websocket path is: a deposit rejected
      // before its L1 tx was ever submitted has no UTxO to refund.
      return rejectionUpdate(tx, polled.failureReason);
    }
  }

  if (polled.status?.type !== "HARD_CONFIRMED") return null;
  const update: Partial<IDepositHistoryTx> = { hardConfirmed: true };
  // Promote only pre-confirmation statuses; never downgrade an entry that
  // has already progressed (settling, on-chain, refund flow, ...).
  if (tx.status === "new" || tx.status === "pending") {
    update.status = "confirmed";
  }
  return update;
}

/**
 * Should the reconciler submit the refund tx for a rejected deposit now?
 *
 * `status === "rejected"` already implies the deposit's L1 tx was submitted:
 * rejectionUpdate only assigns "rejected" when a hash is present, and a
 * deposit rejected before it ever reached L1 becomes "failed" instead. So the
 * deposit UTxO that the refund spends exists.
 *
 * `refundValid` gates on the refund tx's validity-start interval. Hydrozoa
 * post-dates the refund, and the node rejects a tx submitted before its
 * validity start (OutsideValidityInterval), so we hold off until it is valid.
 * Submitting flips the status to "refunding", which drops the deposit out of
 * this predicate and prevents a double-submit.
 */
export function shouldSubmitRefund(
  tx: Pick<IDepositHistoryTx, "status" | "refundCbor">,
  refundValid: boolean,
): boolean {
  return tx.status === "rejected" && !!tx.refundCbor && refundValid;
}

/**
 * Status update for a deposit the ledger reports as rejected. Hash-aware: the
 * refund tx spends the L1 deposit UTxO, so it only makes sense once the
 * deposit was actually submitted (hash set). A deposit rejected before it
 * ever reached L1 has nothing to refund — mark it failed so the reconciler
 * does not steer it into the refund-submission flow (which would try to spend
 * a UTxO that never existed, and surface a spurious "Refunding" status).
 */
export function rejectionUpdate(
  tx: Pick<IDepositHistoryTx, "hash">,
  failureReason?: string | null,
): Partial<IDepositHistoryTx> {
  if (tx.hash) {
    return {
      status: "rejected",
      errorMessage: failureReason ?? "Deposit was rejected by the network.",
    };
  }
  return {
    status: "failed",
    errorMessage:
      failureReason ??
      "Deposit was rejected before the L1 transaction was submitted.",
  };
}

/**
 * Should the poller keep hitting GET /head/requests/{id} for this deposit?
 *
 * Until the deposit reaches a decision. It used to stop as soon as the refund
 * was in hand and the block was hard confirmed, on the grounds that absorption
 * would arrive over the websocket -- which left an on-chain deposit with
 * nothing watching for its absorption at all.
 *
 * That is the ordinary path, not a rare one: a deposit takes minutes to
 * settle, so the tab is usually closed when absorption happens. There is no
 * socket to receive the push, the entry reloads from localStorage still saying
 * "on-chain", and nothing ever asks again. Reconnecting does not necessarily
 * rescue it either, since the deposits snapshot rides a PRIVATE channel that
 * needs a wallet signature to subscribe; this poll is an unauthenticated GET
 * and works either way.
 */
export function needsRequestPolling(tx: IDepositHistoryTx): boolean {
  if (DECISION_TERMINAL_STATUSES.includes(tx.status)) {
    // "rejected" is terminal for the decision but not for the refund: keep
    // polling until the refund tx is in hand, so the refund flow can start.
    return tx.status === "rejected" && !tx.refundCbor;
  }
  return true;
}

/**
 * Per-stage timeline states for the deposit modal.
 *
 * "Approved" and "Refund provisioned" are independent prerequisites: the
 * refund can be polled from hydrozoa before the block is hard confirmed,
 * and hard confirmation can beat the poller. "Submitted" only starts once
 * both are green (matching the submission gate above).
 */
export function getDepositStageStates(tx: TStageInput): IDepositStageStates {
  if (tx.status === "absorbed") {
    return { approved: "done", refund: "done", submitted: "done", available: "done" };
  }
  const confirmed = isHardConfirmed(tx);
  const refundReady = hasProvisionedRefund(tx);
  const submittedDone = tx.status === "on-chain";
  return {
    approved: confirmed ? "done" : "active",
    refund: refundReady ? "done" : confirmed ? "active" : "waiting",
    submitted: submittedDone
      ? "done"
      : tx.status === "settling" || (confirmed && refundReady)
        ? "active"
        : "waiting",
    available: submittedDone ? "active" : "waiting",
  };
}

export interface IRefundStageStates {
  /** The refund tx's post-dated validity window has opened (submission begun). */
  window: TStageState;
  /** The refund tx has been submitted to L1. */
  submitted: TStageState;
  /** The refund tx is confirmed on L1, funds returned to the wallet. */
  onChain: TStageState;
}

/**
 * A deposit in the refund flow: it was rejected *after* its L1 tx reached the
 * chain, so a refund UTxO exists to spend back to the wallet. A deposit
 * rejected before L1 becomes "failed" (nothing to refund) and is NOT part of
 * this flow — see rejectionUpdate.
 */
export function isRefundFlow(status: TDepositHistoryStatus): boolean {
  return (
    status === "rejected" || status === "refunding" || status === "refunded"
  );
}

/**
 * Per-stage timeline states for a deposit's refund, mirroring
 * getDepositStageStates but for the reject → refund path.
 *
 * - rejected:  the refund is post-dated; waiting for its validity window to
 *              open (the reconciler submits automatically once it does).
 * - refunding: the refund tx has been submitted; awaiting L1 confirmation.
 * - refunded:  the refund is confirmed on L1.
 */
export function getRefundStageStates(
  status: TDepositHistoryStatus,
): IRefundStageStates {
  if (status === "refunded") {
    return { window: "done", submitted: "done", onChain: "done" };
  }
  if (status === "refunding") {
    return { window: "done", submitted: "done", onChain: "active" };
  }
  // "rejected" (and, defensively, any other status routed here): the refund is
  // waiting on its validity window before it can be submitted.
  return { window: "active", submitted: "waiting", onChain: "waiting" };
}

/** Grace after the TTL before we call a deposit dead. A tx included in the very
 *  last valid block still has to propagate and be seen by the provider. */
const TTL_GRACE_MS = 60_000;

/**
 * The deposit tx's own deadline, as a unix ms timestamp.
 *
 * Read from the signed CBOR rather than stored alongside it, so it cannot drift
 * from the bytes the chain will actually judge. Returns undefined if the tx
 * carries no TTL, in which case the caller has no deadline to enforce.
 */
export function depositTxExpiryMs(
  cbor: string,
  slotToUnix: (slot: number) => number,
): number | undefined {
  try {
    const ttl = Core.Transaction.fromCbor(Core.TxCBOR(cbor)).body().ttl();
    if (ttl === undefined) return undefined;
    return slotToUnix(Number(ttl));
  } catch {
    return undefined;
  }
}

/**
 * Whether a deposit tx can still reach the chain.
 *
 * Past its TTL a tx can never be included, so neither re-submitting it nor
 * watching for it is any use -- that is the point at which an unconfirmed
 * deposit becomes genuinely failed rather than merely slow.
 */
export function depositTxCanStillLand(
  expiryMs: number | undefined,
  now: number,
): boolean {
  if (expiryMs === undefined) return false;
  return now < expiryMs + TTL_GRACE_MS;
}
